/**
 * DocumentsPage - List of uploaded documents with filtering and actions
 */
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, toast } from '@/components/ui';
import { DocumentStatusBadge, DeleteDocumentDialog } from '@/components/documents';
import { useDocuments, useDeleteDocument, useRetryDocument, useDocumentCounts } from '@/hooks';
import { formatDate, formatFileSize, getFileTypeLabel } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { Document, DocumentStatusFilter, DocumentStatus, DocumentCounts } from '@/types/document';

const PAGE_SIZE = 20;

// Document statuses in which processing has finished and the staged import can
// be reviewed and committed (WP-BUG-67: the server refuses a commit before the
// document is 'staged'). 'pending_account' and 'pending_review' are the
// frontend's names for the review steps.
const READY_FOR_REVIEW_STATUSES: readonly DocumentStatus[] = ['staged', 'pending_account', 'pending_review'];

/**
 * Status filter tabs configuration
 */
interface StatusFilterConfig {
  value: DocumentStatusFilter;
  label: string;
  countKey: keyof DocumentCounts;
}

const STATUS_FILTERS: StatusFilterConfig[] = [
  { value: 'all', label: 'All', countKey: 'all' },
  { value: 'processing', label: 'Processing', countKey: 'processing' },
  { value: 'pending_review', label: 'Pending Review', countKey: 'pending_review' },
  { value: 'completed', label: 'Completed', countKey: 'completed' },
  { value: 'failed', label: 'Failed', countKey: 'failed' },
];

/**
 * Map UI filter to API status parameter
 */
function mapFilterToApiStatus(filter: DocumentStatusFilter): DocumentStatus | undefined {
  if (filter === 'all') return undefined;
  // For pending_review, we want both pending_account and pending_review
  // Backend should handle this, we just pass the filter value
  return filter as DocumentStatus;
}

/**
 * File type icon component
 */
function FileTypeIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  // Different icons based on file type
  const isPdf = mimeType === 'application/pdf';
  const isImage = mimeType.startsWith('image/');
  const isSpreadsheet =
    mimeType.includes('csv') ||
    mimeType.includes('excel') ||
    mimeType.includes('spreadsheet');

  if (isPdf) {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 13h6M9 17h4"
        />
      </svg>
    );
  }

  if (isImage) {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    );
  }

  if (isSpreadsheet) {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    );
  }

  // Default document icon
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Upload icon for buttons
 */
function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
      />
    </svg>
  );
}

/**
 * Empty state component
 */
function DocumentsEmptyState({
  hasFilter,
  onUpload,
  onClearFilter,
}: {
  hasFilter: boolean;
  onUpload: () => void;
  onClearFilter: () => void;
}) {
  if (hasFilter) {
    return (
      <Card className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No documents found</h3>
        <p className="mt-2 text-sm text-gray-500">
          No documents match the current filter.
        </p>
        <div className="mt-6">
          <Button variant="outline" onClick={onClearFilter}>
            Clear Filter
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="text-center py-12">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3 className="mt-4 text-lg font-medium text-gray-900">No documents uploaded yet</h3>
      <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
        Upload bank statements, CSVs, or financial exports to import your transactions.
      </p>
      <div className="mt-6">
        <Button onClick={onUpload}>
          <UploadIcon className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>
    </Card>
  );
}

/**
 * Loading skeleton for the documents list
 */
function DocumentsListSkeleton() {
  return (
    <Card padding="none">
      <div className="divide-y divide-gray-100">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="p-4 animate-pulse">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gray-200 rounded mr-4" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-48 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-24" />
              </div>
              <div className="h-6 bg-gray-200 rounded w-20" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Single document row component
 */
function DocumentRow({
  document,
  onResume,
  onRetry,
  onDelete,
  isRetrying = false,
}: {
  document: Document;
  onResume: () => void;
  onRetry: () => void;
  onDelete: () => void;
  isRetrying?: boolean;
}) {
  // Can resume if document has been processed and is ready for user action
  // - staged: Ready for review
  // - parsed: Parsing complete, needs staging/review
  // - classified: Classification complete, can continue to parsing
  // - pending_account/pending_review: In account mapping flow
  const canResume =
    document.status === 'staged' ||
    document.status === 'parsed' ||
    document.status === 'classified' ||
    document.status === 'pending_account' ||
    document.status === 'pending_review';
  // Show retry/reprocess for failed or error. Not for 'ingested': since the
  // household processing cap (WP-SEC-19), an ingested document is queued or
  // waiting for one of the household's two processing slots and starts on its
  // own; reprocessing it would only answer 409.
  const canRetry = document.status === 'failed' || document.status === 'error';
  const canDelete = document.status !== 'processing' && document.status !== 'uploading';

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* Document info */}
      <td className="py-4 px-4 sm:px-6">
        <div className="flex items-center">
          <FileTypeIcon
            mimeType={document.mimeType}
            className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 mr-3 flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate max-w-[200px] sm:max-w-none">
              {document.filename}
            </p>
            <p className="text-sm text-gray-500">
              {document.sizeBytes ? formatFileSize(document.sizeBytes) : ''}
            </p>
          </div>
        </div>
      </td>

      {/* Type - hidden on mobile */}
      <td className="py-4 px-4 hidden md:table-cell">
        <span className="text-sm text-gray-600">{getFileTypeLabel(document.mimeType)}</span>
      </td>

      {/* Status */}
      <td className="py-4 px-4">
        <div>
          <DocumentStatusBadge status={document.status} />
          {document.errorMessage && (
            <p className="text-xs text-red-600 mt-1 max-w-[150px] truncate" title={document.errorMessage}>
              {document.errorMessage}
            </p>
          )}
        </div>
      </td>

      {/* Date - hidden on mobile */}
      <td className="py-4 px-4 hidden sm:table-cell">
        <span className="text-sm text-gray-600">{formatDate(document.createdAt)}</span>
      </td>

      {/* Actions */}
      <td className="py-4 px-4 text-right">
        <div className="flex items-center justify-end gap-2">
          {canResume && (
            <Button variant="ghost" size="sm" onClick={onResume}>
              Resume
            </Button>
          )}
          {canRetry && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRetry}
              isLoading={isRetrying}
              disabled={isRetrying}
            >
              {isRetrying ? 'Processing...' : 'Retry'}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={onDelete}
              disabled={isRetrying}
            >
              Delete
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Documents list table component
 */
function DocumentsList({
  documents,
  onResume,
  onRetry,
  onDelete,
  processingDocId,
}: {
  documents: Document[];
  onResume: (doc: Document) => void;
  onRetry: (doc: Document) => void;
  onDelete: (doc: Document) => void;
  processingDocId: string | null;
}) {
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="py-3 px-4 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Document
              </th>
              <th
                scope="col"
                className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell"
              >
                Type
              </th>
              <th
                scope="col"
                className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell"
              >
                Uploaded
              </th>
              <th scope="col" className="py-3 px-4 text-right">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                document={doc}
                onResume={() => onResume(doc)}
                onRetry={() => onRetry(doc)}
                onDelete={() => onDelete(doc)}
                isRetrying={processingDocId === doc.id}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * Pagination component
 */
function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);

  if (total <= pageSize) {
    return (
      <div className="mt-4 text-sm text-gray-500">
        Showing {total} document{total !== 1 ? 's' : ''}
      </div>
    );
  }

  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-sm text-gray-500">
        Showing {start}-{end} of {total} documents
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

/**
 * Main DocumentsPage component
 */
export default function DocumentsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<DocumentStatusFilter>('all');
  const [page, setPage] = useState(0);
  const [deleteDialogDoc, setDeleteDialogDoc] = useState<Document | null>(null);
  // Track which document is currently being processed (for loading state)
  const [processingDocId, setProcessingDocId] = useState<string | null>(null);

  // Fetch documents with filter and pagination
  const apiStatus = mapFilterToApiStatus(statusFilter);
  const { data, isLoading, error, refetch } = useDocuments({
    status: apiStatus,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  // Mutations
  const deleteDocument = useDeleteDocument();
  const retryDocument = useRetryDocument();

  // Fetch document counts for tab badges
  const { data: counts, isLoading: isCountsLoading } = useDocumentCounts();

  // Filter documents client-side for pending_review
  // Backend returns documents with raw status 'staged', but we filter for UI-friendly statuses
  const filteredDocuments = useMemo(() => {
    if (!data?.documents) return [];
    // For pending_review filter, include:
    // - 'staged' (backend status for pending review)
    // - 'pending_account' and 'pending_review' (frontend-only statuses)
    if (statusFilter === 'pending_review') {
      return data.documents.filter((doc) =>
        doc.status === 'staged' ||
        doc.status === 'pending_account' ||
        doc.status === 'pending_review'
      );
    }
    return data.documents;
  }, [data?.documents, statusFilter]);

  // Handlers
  const handleUpload = useCallback(() => {
    navigate('/import');
  }, [navigate]);

  const handleResume = useCallback(
    (doc: Document) => {
      // WP-BUG-67: open the staged import directly only once the document is
      // ready for review. A 'parsed' document already names its staged import,
      // but the server refuses to commit it until processing finishes; the
      // documentId route polls until then.
      if (doc.stagedImportId && READY_FOR_REVIEW_STATUSES.includes(doc.status)) {
        navigate(`/import?id=${doc.stagedImportId}`);
      } else {
        navigate(`/import?documentId=${doc.id}`);
      }
    },
    [navigate]
  );

  const handleRetry = useCallback(
    async (doc: Document) => {
      // Check if document is already processing
      if (doc.status === 'processing') {
        toast.warning('Document is already being processed');
        return;
      }

      // Set loading state for this document
      setProcessingDocId(doc.id);

      try {
        await retryDocument.mutateAsync(doc.id);
        toast.success('Document queued for processing');
      } catch (err) {
        // Show error toast with message
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to process document';
        toast.error(errorMessage);
      } finally {
        // Clear loading state
        setProcessingDocId(null);
      }
    },
    [retryDocument]
  );

  const handleDeleteClick = useCallback((doc: Document) => {
    setDeleteDialogDoc(doc);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialogDoc) return;
    try {
      await deleteDocument.mutateAsync(deleteDialogDoc.id);
      setDeleteDialogDoc(null);
    } catch {
      // Error is handled by React Query - could show toast notification
    }
  }, [deleteDialogDoc, deleteDocument]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogDoc(null);
  }, []);

  const handleFilterChange = useCallback((filter: DocumentStatusFilter) => {
    setStatusFilter(filter);
    setPage(0); // Reset to first page when filter changes
  }, []);

  const handleClearFilter = useCallback(() => {
    setStatusFilter('all');
    setPage(0);
  }, []);

  // Error state
  if (error) {
    return (
      <div className="max-w-5xl mx-auto">
        <Card className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Unable to Load Documents</h3>
          <p className="mt-2 text-sm text-gray-500">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <div className="mt-6">
            <Button onClick={() => refetch()}>Try Again</Button>
          </div>
        </Card>
      </div>
    );
  }

  const documents = filteredDocuments;
  const total = data?.total ?? 0;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          {!isLoading && (
            <p className="mt-1 text-sm text-gray-500">
              {total} document{total !== 1 ? 's' : ''} uploaded
            </p>
          )}
        </div>
        <Button onClick={handleUpload}>
          <UploadIcon className="w-4 h-4 mr-2" />
          Upload
        </Button>
      </div>

      {/* Status filter tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Filter by status">
          {STATUS_FILTERS.map((filter) => {
            const count = counts?.[filter.countKey];
            return (
              <button
                key={filter.value}
                onClick={() => handleFilterChange(filter.value)}
                className={cn(
                  'py-2 px-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap',
                  statusFilter === filter.value
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
                aria-current={statusFilter === filter.value ? 'page' : undefined}
              >
                {filter.label}
                {!isCountsLoading && count !== undefined && (
                  <span className="ml-1">({count})</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      {isLoading ? (
        <DocumentsListSkeleton />
      ) : documents.length === 0 ? (
        <DocumentsEmptyState
          hasFilter={statusFilter !== 'all'}
          onUpload={handleUpload}
          onClearFilter={handleClearFilter}
        />
      ) : (
        <>
          <DocumentsList
            documents={documents}
            onResume={handleResume}
            onRetry={handleRetry}
            onDelete={handleDeleteClick}
            processingDocId={processingDocId}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Delete confirmation dialog */}
      <DeleteDocumentDialog
        isOpen={deleteDialogDoc !== null}
        document={deleteDialogDoc}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={deleteDocument.isPending}
      />
    </div>
  );
}
